import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, IconButton, Tooltip, CircularProgress
} from '@mui/material';
import {
  MonetizationOn, Receipt, CalculateOutlined, BarChart, EventRepeat,
  Settings, Home, Refresh, Code, People
} from '@mui/icons-material';
import axios from 'axios';
import { fmtN, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PayrollDashboard = () => {
  const navigate  = useNavigate();
  const [stats,   setStats]   = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [runsRes] = await Promise.all([
        axios.get(`${API}/api/payroll/runs`, { headers: hdrs() }).catch(() => ({ data: [] })),
      ]);
      const runs    = runsRes.data || [];
      const pending = runs.filter(r => ['Open', 'Calculated', 'Pending Approval'].includes(r.status)).length;
      const posted  = runs.filter(r => r.status === 'Posted').length;
      const lastRun = runs.find(r => ['Approved', 'Posted'].includes(r.status));
      setStats({
        totalRuns: runs.length,
        pending,
        posted,
        lastHeadCount: lastRun?.headCount  || 0,
        lastGross:     lastRun?.totalGross  || 0,
        lastRunName:   lastRun?.periodName  || '—',
      });
    } catch {
      setStats({ totalRuns: 0, pending: 0, posted: 0, lastHeadCount: 0, lastGross: 0, lastRunName: '—' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const kpis = [
    { label: 'Pending Runs',        value: stats.pending        ?? 0,  color: '#e65100', bg: '#fff3e0' },
    { label: 'Posted This Year',    value: stats.posted         ?? 0,  color: '#1565c0', bg: '#e3f2fd' },
    { label: 'Employees (Last Run)',value: stats.lastHeadCount  ?? 0,  color: '#2e7d32', bg: '#e8f5e9' },
    { label: 'Total Runs',          value: stats.totalRuns      ?? 0,  color: '#546e7a', bg: '#eceff1' },
  ];

  const modules = [
    { icon: People,            label: 'Employees',            desc: 'View employees, manage pay groups, grades and pay lines', path: '/payroll/employees',          color: '#00695c' },
    { icon: CalculateOutlined, label: 'Payroll Runs',        desc: 'Process, calculate, approve and post monthly payroll', path: '/payroll/runs',               color: '#1565c0' },
    { icon: Receipt,           label: 'Payslips',            desc: 'View and print employee payslips',                     path: '/payroll/runs',               color: '#2e7d32' },
    { icon: Code,              label: 'Transaction Codes',   desc: 'Formula-driven earnings and deductions setup',          path: '/payroll/setup/transactions', color: '#e65100' },
    { icon: Settings,          label: 'Payroll Setup',       desc: 'PAYE bands, statutory rates and NSSF configuration',   path: '/payroll/setup',              color: '#263238' },
    { icon: EventRepeat,       label: 'Payroll Periods',     desc: 'Manage monthly payroll periods',                       path: '/payroll/periods',            color: '#6a1b9a' },
    { icon: BarChart,          label: 'Payroll Reports',     desc: 'Register, statutory reports, P9A and bank file',       path: '/payroll/reports',            color: '#00838f' },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2,
        }}>
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
              <MonetizationOn sx={{ fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700}>Payroll</Typography>
            </Box>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Payroll Runs · PAYE · SHIF · Housing Levy · NSSF · Payslips
            </Typography>
            {stats.lastRunName !== '—' && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Last processed: {stats.lastRunName} — {sym()} {fmtN(stats.lastGross)} gross
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={load} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Home">
              <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* KPI tiles */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Overview</Typography>
        <Grid container spacing={2} mb={3}>
          {kpis.map(({ label, value, color, bg }) => (
            <Grid item xs={6} md={3} key={label}>
              <Card elevation={2} sx={{ borderRadius: 3, borderLeft: `4px solid ${color}` }}>
                <CardContent sx={{ py: 1.8 }}>
                  <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>
                    {loading ? '…' : value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Module tiles */}
        <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Modules</Typography>
        <Grid container spacing={2}>
          {modules.map(({ icon: Icon, label, desc, path, color }) => (
            <Grid item xs={12} sm={6} key={label}>
              <Card sx={{
                borderRadius: 3, cursor: 'pointer', borderTop: `4px solid ${color}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' },
              }} onClick={() => navigate(path)}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{
                      width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${color}1A`,
                    }}>
                      <Icon sx={{ fontSize: 24, color }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                      <Typography variant="body2" color="text.secondary" fontSize={12} mt={0.3}>{desc}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

      </Box>
    </Box>
  );
};

export default PayrollDashboard;
